import type { API, FileInfo, JSCodeshift, Options } from 'jscodeshift';

import { reportManualReview } from '../../controller/helpers';
import {
  addAttributesIfMissing,
  addImport,
  createReporter,
  findJSXElementsByName,
  hasAttributeOnElement,
  hasImport,
  processIconChildren,
  removeAttributeByName,
  setNameIfJSXIdentifier,
} from '../../helpers/jscodeshift';

export const parser = 'tsx';

interface LegacyProps {
  priority?: string;
  size?: string;
  type?: string;
  htmlType?: string;
  sentiment?: string;
  [key: string]: unknown;
}

interface ExtendedOptions extends Options {
  accentSecondaryMapping?: string;
  positiveSecondaryMapping?: string;
}

type PriorityMapping = Record<string, Record<string, string>>;

const buildPriorityMapping = (opts: Options): PriorityMapping => {
  const extendedOpts = opts as ExtendedOptions;
  const accentSecondary = extendedOpts.accentSecondaryMapping || 'secondary-neutral';
  const positiveSecondary = extendedOpts.positiveSecondaryMapping || 'secondary-neutral';
  return {
    accent: {
      primary: 'primary',
      secondary: accentSecondary,
      tertiary: 'tertiary',
    },
    positive: {
      primary: 'primary',
      secondary: positiveSecondary,
      tertiary: positiveSecondary,
    },
    negative: {
      primary: 'primary',
      secondary: 'secondary',
      tertiary: 'secondary',
    },
    primary: {
      primary: 'primary',
      secondary: accentSecondary,
      tertiary: 'tertiary',
    },
    pay: {
      primary: 'primary',
      secondary: accentSecondary,
      tertiary: accentSecondary,
    },
  };
};

const sizeMap: Record<string, string> = {
  EXTRA_SMALL: 'xs',
  SMALL: 'sm',
  MEDIUM: 'md',
  LARGE: 'lg',
  EXTRA_LARGE: 'xl',
  xs: 'sm',
  sm: 'sm',
  md: 'md',
  lg: 'lg',
  xl: 'xl',
};

const resolveSize = (size?: string): string | undefined => {
  if (!size) return size;
  const match = /^Size\.(EXTRA_SMALL|SMALL|MEDIUM|LARGE|EXTRA_LARGE)$/u.exec(size);
  if (match) {
    return sizeMap[match[1]];
  }
  return sizeMap[size] || size;
};

const legacyButtonTypes = [
  'accent',
  'negative',
  'positive',
  'primary',
  'pay',
  'secondary',
  'danger',
  'link',
];

const getConsistentTypeConversions = (
  type?: string,
): { priority?: string; sentiment?: string } | null => {
  const consistentTypeConversions: Record<string, { priority?: string; sentiment?: string }> = {
    secondary: { priority: 'secondary-neutral' },
    link: { priority: 'tertiary' },
    danger: { priority: 'secondary', sentiment: 'negative' },
  };

  return consistentTypeConversions[type || ''] || null;
};

const convertEnumValue = (value?: string): string | undefined => {
  if (!value) return value;
  const strippedValue = value.replace(/^['"]|['"]$/gu, '');
  const enumMapping: Record<string, string> = {
    'Priority.SECONDARY': 'secondary',
    'Priority.PRIMARY': 'primary',
    'Priority.TERTIARY': 'tertiary',
    'ControlType.NEGATIVE': 'negative',
    'ControlType.POSITIVE': 'positive',
    'ControlType.ACCENT': 'accent',
  };
  return enumMapping[strippedValue] || strippedValue;
};

/**
 * Detects if a value is an enum pattern (e.g., Priority.PRIMARY, ControlType.ACCENT, Size.LARGE, Type.PRIMARY)
 */
const isEnumValue = (value: string): boolean => {
  const enumPatterns = [
    /^Priority\.(PRIMARY|SECONDARY|TERTIARY|SECONDARY_NEUTRAL)$/u,
    /^ControlType\.(ACCENT|NEGATIVE|POSITIVE)$/u,
    /^Size\.(EXTRA_SMALL|SMALL|MEDIUM|LARGE|EXTRA_LARGE)$/u,
    /^Type\.(PRIMARY|SECONDARY|TERTIARY|PAY|DANGER|LINK|ACCENT|POSITIVE|NEGATIVE)$/u,
  ];
  return enumPatterns.some((pattern) => pattern.test(value));
};

/**
 * Maps enum values to their expected string equivalents for validation purposes
 * This is ONLY used to validate the enum maps to a supported value
 */
const getEnumEquivalent = (value: string): string | undefined => {
  const enumMapping: Record<string, string> = {
    'Priority.PRIMARY': 'primary',
    'Priority.SECONDARY': 'secondary',
    'Priority.TERTIARY': 'tertiary',
    'Priority.SECONDARY_NEUTRAL': 'secondary-neutral',
    'ControlType.NEGATIVE': 'negative',
    'ControlType.POSITIVE': 'positive',
    'ControlType.ACCENT': 'accent',
    'Size.EXTRA_SMALL': 'sm',
    'Size.SMALL': 'sm',
    'Size.MEDIUM': 'md',
    'Size.LARGE': 'lg',
    'Size.EXTRA_LARGE': 'xl',
  };
  return enumMapping[value];
};

/**
 * This transform function modifies the Button and ActionButton components from the @transferwise/components library.
 * It updates the ActionButton component to use the Button component with specific attributes and mappings.
 * It also processes icon children and removes legacy props.
 *
 * @param {FileInfo} file - The file information object.
 * @param {API} api - The API object for jscodeshift.
 * @param {Options} options - The options object for jscodeshift.
 * @returns {string} - The transformed source code.
 */
const transformer = (file: FileInfo, api: API, options: Options) => {
  const j: JSCodeshift = api.jscodeshift;
  const root = j(file.source);
  const manualReviewIssues: string[] = [];
  const priorityMapping = buildPriorityMapping(options);

  const resolvePriority = (type?: string, priority?: string): string | undefined => {
    if (type && priority) {
      return priorityMapping[type]?.[priority] || priority;
    }
    return priority;
  };

  // Create reporter instance
  const reporter = createReporter(j, manualReviewIssues);

  const {
    exists: hasButtonImport,
    aliases: buttonAliases,
    resolvedName: buttonName,
    conflictingImports: conflictingButtonImport,
  } = hasImport(root, '@transferwise/components', 'Button', j);

  if (conflictingButtonImport.length) {
    conflictingButtonImport.forEach((node) => reporter.reportConflictingImports(node));
  }

  const {
    exists: hasActionButtonImport,
    remove: removeActionButtonImport,
    aliases: actionButtonAliases,
  } = hasImport(root, '@transferwise/components', 'ActionButton', j);

  if (!hasButtonImport && !hasActionButtonImport) {
    return file.source;
  }

  const iconImports = new Set<string>();
  root.find(j.ImportDeclaration, { source: { value: '@transferwise/icons' } }).forEach((path) => {
    path.node.specifiers?.forEach((specifier) => {
      if (
        (specifier.type === 'ImportDefaultSpecifier' || specifier.type === 'ImportSpecifier') &&
        specifier.local
      ) {
        const localName = (specifier.local as { name: string }).name;
        iconImports.add(localName);
      }
    });
  });

  if (hasActionButtonImport) {
    if (!hasButtonImport) {
      addImport(root, '@transferwise/components', 'Button', j);
    }

    const instances = findJSXElementsByName(root, j)('ActionButton', actionButtonAliases);

    instances.forEach((path) => {
      const { openingElement, closingElement } = path.node;

      openingElement.name = setNameIfJSXIdentifier(openingElement.name, buttonName)!;
      if (closingElement) {
        closingElement.name = setNameIfJSXIdentifier(closingElement.name, buttonName)!;
      }

      processIconChildren(j, path.node.children, iconImports, openingElement);

      if ((openingElement.attributes ?? []).some((attr) => attr.type === 'JSXSpreadAttribute')) {
        reporter.reportSpreadProps(path);
      }

      const legacyPropNames = ['priority', 'text', 'size'];
      const legacyProps: LegacyProps = {};

      openingElement.attributes?.forEach((attr) => {
        if (attr.type === 'JSXAttribute' && attr.name?.type === 'JSXIdentifier') {
          const { name } = attr.name;
          if (legacyPropNames.includes(name)) {
            if (attr.value) {
              if (attr.value.type === 'StringLiteral') {
                legacyProps[name] = attr.value.value;
              } else if (attr.value.type === 'JSXExpressionContainer') {
                legacyProps[name] = convertEnumValue(String(j(attr.value.expression).toSource()));
              }
            }
          }
        }
      });

      const hasTextProp = 'text' in legacyProps;
      const hasChildren =
        path.node.children?.some(
          (child) =>
            (child.type === 'JSXText' && child.value.trim() !== '') ||
            child.type === 'JSXElement' ||
            child.type === 'JSXExpressionContainer',
        ) ||
        (path.node.children && path.node.children?.length > 0);

      if (hasTextProp && hasChildren) {
        reporter.reportPropWithChildren(path, 'text');
      } else if (hasTextProp && !hasChildren && openingElement.selfClosing) {
        // Self-closing tag with text prop but no children, so we can convert to a normal element with children
        path.replace(
          j.jsxElement(
            j.jsxOpeningElement(openingElement.name, openingElement.attributes),
            j.jsxClosingElement(openingElement.name),
            [j.jsxText((legacyProps.text as string) || '')],
          ),
        );
      }

      addAttributesIfMissing(j, path.node.openingElement, [
        { attribute: j.jsxAttribute(j.jsxIdentifier('v2')), name: 'v2' },
        { attribute: j.jsxAttribute(j.jsxIdentifier('size'), j.literal('sm')), name: 'size' },
      ]);

      (path.node.children || []).forEach((child) => {
        if (child.type === 'JSXExpressionContainer') {
          const expr = child.expression;
          if (
            expr.type === 'ConditionalExpression' ||
            expr.type === 'CallExpression' ||
            expr.type === 'Identifier' ||
            expr.type === 'MemberExpression'
          ) {
            reporter.reportAmbiguousChildren(path, 'icon');
          }
        }
      });
    });

    removeActionButtonImport();
  }

  if (hasButtonImport) {
    const instances = findJSXElementsByName(root, j)('Button', buttonAliases);

    instances.forEach((path) => {
      const { openingElement } = path.node;

      if (hasAttributeOnElement(openingElement, 'v2')) return;

      const hasJSXChildren = path.node.children?.some(
        (child) =>
          (child.type === 'JSXText' && child.value.trim() !== '') ||
          child.type === 'JSXElement' ||
          (child.type === 'JSXFragment' && child.children && child.children.length > 0) ||
          (child.type === 'JSXExpressionContainer' &&
            child.expression.type !== 'JSXEmptyExpression'),
      );
      const hasChildrenAsProp = openingElement.attributes?.some(
        (attr) =>
          attr.type === 'JSXAttribute' &&
          attr.name?.type === 'JSXIdentifier' &&
          attr.name.name === 'children',
      );
      if (!hasJSXChildren && !hasChildrenAsProp) return;

      addAttributesIfMissing(j, openingElement, [
        { attribute: j.jsxAttribute(j.jsxIdentifier('v2')), name: 'v2' },
      ]);
      processIconChildren(j, path.node.children, iconImports, openingElement);

      const legacyProps: LegacyProps = {};
      const legacyPropNames = ['priority', 'size', 'type', 'htmlType', 'sentiment'];

      openingElement.attributes?.forEach((attr) => {
        if (attr.type === 'JSXAttribute' && attr.name?.type === 'JSXIdentifier') {
          const { name } = attr.name;
          if (legacyPropNames.includes(name)) {
            if (attr.value) {
              if (attr.value.type === 'StringLiteral') {
                legacyProps[name] = attr.value.value;
              } else if (attr.value.type === 'JSXExpressionContainer') {
                const expressionSource = String(j(attr.value.expression).toSource());
                legacyProps[name] = expressionSource;
              }
            } else {
              legacyProps[name] = undefined;
            }
          }
        }
      });

      if ('size' in legacyProps) {
        const rawValue = legacyProps.size;

        if (typeof rawValue === 'string' && isEnumValue(rawValue)) {
          const equivalent = getEnumEquivalent(rawValue);
          const supportedSizes = ['sm', 'md', 'lg', 'xl'];

          if (equivalent && supportedSizes.includes(equivalent)) {
          } else {
            reporter.reportUnsupportedValue(path, 'size', rawValue);
          }
        } else {
          const resolved = resolveSize(rawValue);
          const supportedSizes = ['sm', 'md', 'lg', 'xl'];
          if (
            typeof rawValue === 'string' &&
            typeof resolved === 'string' &&
            supportedSizes.includes(resolved)
          ) {
            removeAttributeByName(j, openingElement, 'size');
            openingElement.attributes?.push(
              j.jsxAttribute(j.jsxIdentifier('size'), j.literal(resolved)),
            );
          } else if (typeof rawValue === 'string') {
            reporter.reportUnsupportedValue(path, 'size', rawValue);
          } else if (rawValue !== undefined) {
            reporter.reportAmbiguousExpression(path, 'size');
          }
        }
      }

      if ('priority' in legacyProps) {
        const rawValue = legacyProps.priority;

        if (typeof rawValue === 'string' && isEnumValue(rawValue)) {
          const equivalent = getEnumEquivalent(rawValue);
          const supportedPriorities = ['primary', 'secondary', 'tertiary', 'secondary-neutral'];

          if (equivalent && supportedPriorities.includes(equivalent)) {
          } else {
            reporter.reportUnsupportedValue(path, 'priority', rawValue);
          }
        } else {
          const converted = convertEnumValue(rawValue);
          const mapped = resolvePriority(legacyProps.type, converted);
          const supportedPriorities = ['primary', 'secondary', 'tertiary', 'secondary-neutral'];
          if (
            typeof rawValue === 'string' &&
            typeof mapped === 'string' &&
            supportedPriorities.includes(mapped)
          ) {
            removeAttributeByName(j, openingElement, 'priority');
            openingElement.attributes?.push(
              j.jsxAttribute(j.jsxIdentifier('priority'), j.literal(mapped)),
            );
          } else if (typeof rawValue === 'string') {
            reporter.reportUnsupportedValue(path, 'priority', rawValue);
          } else if (rawValue !== undefined) {
            reporter.reportAmbiguousExpression(path, 'priority');
          }
        }
      }

      if ('type' in legacyProps || 'htmlType' in legacyProps) {
        const rawType = legacyProps.type;
        const rawHtmlType = legacyProps.htmlType;

        let resolvedType: string | undefined;
        let isTypeEnum = false;
        let isControlTypeEnum = false;

        if (typeof rawType === 'string' && isEnumValue(rawType)) {
          isTypeEnum = true;
          const equivalent = getEnumEquivalent(rawType);
          resolvedType = equivalent;
          isControlTypeEnum = rawType.startsWith('ControlType.');

          if (!isControlTypeEnum) {
            reporter.reportUnsupportedValue(path, 'type', rawType);
          }
        } else {
          let typeValue: string | undefined;
          if (typeof rawType === 'string') {
            typeValue = rawType;
          } else if (rawType && typeof rawType === 'object') {
            typeValue = convertEnumValue(j(rawType).toSource());
          }
          resolvedType = typeValue;
        }

        let finalHtmlType = null;
        if (resolvedType && !legacyButtonTypes.includes(resolvedType)) {
          finalHtmlType = resolvedType;
        }

        if (rawHtmlType) {
          finalHtmlType = rawHtmlType;
        }

        const htmlTypes = ['submit', 'button', 'reset'];

        if (resolvedType === 'negative' && isControlTypeEnum) {
          removeAttributeByName(j, openingElement, 'type');

          if (hasAttributeOnElement(openingElement, 'sentiment')) {
            removeAttributeByName(j, openingElement, 'sentiment');
          }

          addAttributesIfMissing(j, openingElement, [
            {
              attribute: j.jsxAttribute(j.jsxIdentifier('priority'), j.literal('primary')),
              name: 'priority',
            },
          ]);

          // Keep as enum
          if (rawType) {
            openingElement.attributes?.push(
              j.jsxAttribute(
                j.jsxIdentifier('sentiment'),
                j.jsxExpressionContainer(j.identifier(rawType)),
              ),
            );
          }
        } else if (resolvedType === 'negative' && !isTypeEnum) {
          // String literal 'negative'
          removeAttributeByName(j, openingElement, 'type');

          if (hasAttributeOnElement(openingElement, 'sentiment')) {
            removeAttributeByName(j, openingElement, 'sentiment');
          }

          openingElement.attributes?.push(
            j.jsxAttribute(j.jsxIdentifier('sentiment'), j.literal('negative')),
          );
        } else if (
          isControlTypeEnum &&
          resolvedType &&
          ['positive', 'accent'].includes(resolvedType)
        ) {
          removeAttributeByName(j, openingElement, 'type');
          addAttributesIfMissing(j, openingElement, [
            {
              attribute: j.jsxAttribute(j.jsxIdentifier('priority'), j.literal('primary')),
              name: 'priority',
            },
          ]);
        }

        if (
          resolvedType &&
          typeof resolvedType === 'string' &&
          legacyButtonTypes.includes(resolvedType) &&
          !isTypeEnum // Don't convert if it's an enum
        ) {
          const consistentConversion = getConsistentTypeConversions(resolvedType);

          if (consistentConversion) {
            removeAttributeByName(j, openingElement, 'type');
            removeAttributeByName(j, openingElement, 'priority');
            removeAttributeByName(j, openingElement, 'sentiment');

            if (consistentConversion.priority) {
              openingElement.attributes?.push(
                j.jsxAttribute(
                  j.jsxIdentifier('priority'),
                  j.literal(consistentConversion.priority),
                ),
              );
            }
            if (consistentConversion.sentiment) {
              openingElement.attributes?.push(
                j.jsxAttribute(
                  j.jsxIdentifier('sentiment'),
                  j.literal(consistentConversion.sentiment),
                ),
              );
            }
          } else {
            // if priority is present, then type is already handled. if not, add priority. always remove legacy type
            removeAttributeByName(j, openingElement, 'type');
            addAttributesIfMissing(j, openingElement, [
              {
                attribute: j.jsxAttribute(j.jsxIdentifier('priority'), j.literal('primary')),
                name: 'priority',
              },
            ]);
          }
        } else if (isTypeEnum) {
          // Enum value that isn't a supported control type - keep it as-is (already reported above)
          // Don't modify the attribute
        }

        // Handle htmlType conversion to type
        if (typeof finalHtmlType === 'string' && htmlTypes.includes(finalHtmlType)) {
          removeAttributeByName(j, openingElement, 'htmlType');
          openingElement.attributes?.push(
            j.jsxAttribute(j.jsxIdentifier('type'), j.literal(finalHtmlType)),
          );
        } else if (typeof rawType === 'string' || typeof rawHtmlType === 'string') {
          const valueToCheck = rawType ?? rawHtmlType ?? '';
          const legacyTypes = [
            'accent',
            'positive',
            'negative',
            'primary',
            'secondary',
            'danger',
            'link',
          ];

          if (!legacyTypes.includes(valueToCheck)) {
            reporter.reportUnsupportedValue(path, 'type', valueToCheck);
          }
        } else if (rawType !== undefined || rawHtmlType !== undefined) {
          // Report ambiguous if we cannot determine the value
          reporter.reportAmbiguousExpression(
            path,
            typeof rawType === 'string' ? 'type' : 'htmlType',
          );
        }
      }

      // Handle sentiment prop
      if ('sentiment' in legacyProps) {
        const hasSentimentAttribute = openingElement.attributes?.some(
          (attr) => attr.type === 'JSXAttribute' && attr.name?.name === 'sentiment',
        );

        if (!hasSentimentAttribute) {
          const rawValue = legacyProps.sentiment;

          if (rawValue === 'negative') {
            removeAttributeByName(j, openingElement, 'sentiment');
            openingElement.attributes?.push(
              j.jsxAttribute(j.jsxIdentifier('sentiment'), j.literal('negative')),
            );
          } else if (typeof rawValue === 'string') {
            reporter.reportUnsupportedValue(path, 'sentiment', rawValue);
          } else if (rawValue !== undefined) {
            reporter.reportAmbiguousExpression(path, 'sentiment');
          }
        }
      }

      // Handle as prop and href
      let asIndex = -1;
      let asValue: string | null = null;
      let hrefExists = false;
      let asAmbiguous = false;
      let hrefAmbiguous = false;

      openingElement.attributes?.forEach((attr, index) => {
        if (attr.type === 'JSXAttribute' && attr.name) {
          if (attr.name.name === 'as') {
            if (attr.value) {
              if (attr.value.type === 'StringLiteral') {
                asValue = attr.value.value;
              } else if (attr.value.type === 'JSXExpressionContainer') {
                asAmbiguous = true;
                reporter.reportAttribute(attr, path);
              }
            }
            asIndex = index;
          }
          if (attr.name.name === 'href') {
            hrefExists = true;
            if (attr.value && attr.value.type !== 'StringLiteral') {
              hrefAmbiguous = true;
              reporter.reportAttribute(attr, path);
            }
          }
        }
      });

      if (asValue === 'a') {
        if (asIndex !== -1) {
          openingElement.attributes = openingElement.attributes?.filter(
            (_attr, idx) => idx !== asIndex,
          );
        }
        if (!hrefExists) {
          openingElement.attributes = [
            ...(openingElement.attributes ?? []),
            j.jsxAttribute(j.jsxIdentifier('href'), j.literal('#')),
          ];
        }
      }

      if ((openingElement.attributes ?? []).some((attr) => attr.type === 'JSXSpreadAttribute')) {
        reporter.reportSpreadProps(path);
      }
    });
  }

  if (manualReviewIssues.length > 0) {
    manualReviewIssues.forEach(async (issue) => {
      await reportManualReview(file.path, issue);
    });
  }

  return root.toSource();
};

export default transformer;
